import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError, toastSuccess } from "@/lib/toast";
import { postInterest } from "@/lib/interest";

/**
 * Closed-beta register interest form (email + optional message).
 */
export default function RegisterInterestDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setEmail("");
    setBody("");
    setBusy(false);
    setDone(false);
  };

  const handleOpenChange = (next) => {
    if (!next) {
      reset();
    }
    onOpenChange?.(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await postInterest({ email, body });
      setDone(true);
      toastSuccess("Thanks — we received your interest.");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not submit. Try again.";
      toastError(Array.isArray(msg) ? msg.join(", ") : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Register interest</DialogTitle>
          <DialogDescription>
            Circa is in closed beta. Leave your email and optional questions or
            feature ideas — we&apos;ll use this to follow up when invites are
            available.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Thanks for your interest. We&apos;ll be in touch.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="interest-email">Email</Label>
              <Input
                id="interest-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="interest-body">
                Questions or features you&apos;d like to see (optional)
              </Label>
              <textarea
                id="interest-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={busy}
                maxLength={4000}
                rows={4}
                placeholder="What would help your team? Any questions?"
                className="flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !email.trim()}>
                {busy ? "Sending…" : "Submit"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
